/**
 * Types pour le jeu de Tarot (Frontend)
 */

// ============================================
// Types des tables de jeu
// ============================================

/** Types de serveurs/tables */
export type TableServerType = 
  | 'diff_indiv'   // Différé individuel
  | 'diff_tq'      // Différé triplettes/quadrettes
  | 'live'         // Live
  | 'libre'        // Tables libres
  | 'defis';       // Défis

/** Sous-type d'un défi (CIA / championnat / libre / …) */
export type DefiKind =
  | 'cia'
  | 'championnat_equipe'
  | 'par_equipe'
  | 'libre'
  | 'ouvert'
  | 'quadrette'
  | 'entrainement'
  | 'unknown';

/** Catégories de tables pour l'affichage */
export type TableCategory = 
  | 'Live'
  | 'Différé'
  | 'Tables libres'
  | 'Défis';

/** Information d'un joueur sur une table */
export interface TablePlayer {
  position: number;
  pseudo: string;
  pseudo_display?: string;
  elo?: number;
}

/** Mode d'entrée sur une table (legacy) */
export type TableEntryMode = 1 | 2 | 3; // 1=libre, 2=filtrée (maître), 3=mot de passe

/** Une table de jeu */
export interface GameTable {
  idserv: TableServerType;
  ip: string;
  port: number;
  type: string;
  nbj: number;              // Nombre de joueurs attendus (3, 4, 5)
  nomtable?: string;
  comm?: string;
  joueurs: TablePlayer[];
  modeEntree?: TableEntryMode;
  /** Id fft.matchs si disponible (optionnel ; pas publié par Java actuel) */
  sqlIdMatch?: number;
  /** Id fft.c_competition (1=championnat équipes, 2=CIA, …) si connu */
  idCompetition?: number;
  cia?: boolean;
  championnatEquipe?: boolean;
  defiKind?: DefiKind;
  defiKindLabel?: string;
  /** URL résultats déjà écrite par Java (CIA / championnat / coupes) */
  urlResultats?: string;
  /** Poule CIA (enrichissement API) */
  divisionId?: number | null;
  divisionNom?: string | null;
  /** groupe | division | playoff */
  divisionType?: string | null;
  phaseId?: number | null;
  phaseNom?: string | null;
  /** encours | barrage | … */
  phaseEtat?: string | null;
  /** Phase / division de barrage CIA */
  isBarrage?: boolean;
  /** Équipes (championnat / défi par équipe) */
  team1?: { id: number | null; nom: string; photoUrl?: string | null };
  team2?: { id: number | null; nom: string; photoUrl?: string | null };
  /** Rendez-vous confirmé (CIA / championnat par équipe), ISO 8601, si résolu par webtarotAPI */
  rdv?: string;
}

/** Liste des tables groupées par catégorie */
export interface TableList {
  [category: string]: GameTable[];
}

/** Réponse API /api/tables */
export interface TablesApiResponse {
  success: boolean;
  data?: {
    categories: TableList;
    stats: { tables: number; players: number };
    compet: boolean;
  };
  error?: string;
}

// ============================================
// Types de cartes
// ============================================

/** Couleurs des cartes (0-4) */
export enum Suit {
  Hearts = 0,    // Cœur
  Spades = 1,    // Pique
  Diamonds = 2,  // Carreau
  Clubs = 3,     // Trèfle
  Trumps = 4,    // Atouts
}

/** Noms français des couleurs */
export const SuitNames: Record<Suit, string> = {
  [Suit.Hearts]: 'Cœur',
  [Suit.Spades]: 'Pique',
  [Suit.Diamonds]: 'Carreau',
  [Suit.Clubs]: 'Trèfle',
  [Suit.Trumps]: 'Atout',
};

/** Numéro de carte (1-78) */
export type CardNumber = number;

/** Informations décodées d'une carte */
export interface CardInfo {
  number: CardNumber;
  suit: Suit;
  value: number;
  isExcuse: boolean;
  isTrump: boolean;
  isOudler: boolean;
  points: number;
}

// ============================================
// Types d'état de partie
// ============================================

/** États de la partie */
export enum GamePhase {
  Starting = 1,
  Bidding = 2,
  KingCall = 3,
  ViewChien = 4,
  MakeEcart = 5,
  Playing = 6,
  Poignee = 7,
  Finished = 99999,
}

/** Contrats possibles */
export enum Contract {
  Pass = 0,
  Petite = 1,
  Garde = 2,
  GardeSans = 3,
  GardeContre = 4,
}

/** Noms français des contrats */
export const ContractNames: Record<Contract, string> = {
  [Contract.Pass]: 'Passe',
  [Contract.Petite]: 'Petite',
  [Contract.Garde]: 'Garde',
  [Contract.GardeSans]: 'Garde Sans',
  [Contract.GardeContre]: 'Garde Contre',
};

/** Information d'un joueur en partie */
export interface GamePlayer {
  position: number;
  pseudo: string;
  elo?: number;
  isReady: boolean;
  isTaker?: boolean;
  isPartner?: boolean;
  bid?: Contract;
  tricksWon: number;
}

/** État complet d'une partie */
export interface GameState {
  phase: GamePhase;
  tableId: string;
  playerCount: number;
  players: GamePlayer[];
  myPosition: number;
  currentPlayer: number;
  dealer: number;
  currentContract?: Contract;
  taker?: number;
  partner?: number;
  calledKing?: CardNumber;
  myHand: CardNumber[];
  chien?: CardNumber[];
  currentTrick: CardNumber[];
  trickPlayerPositions: number[];  // Position du joueur pour chaque carte du pli
  entameur?: number;               // Joueur qui a entamé le pli courant
  lastTrick?: CardNumber[];
  numPli?: number;  // Numéro du pli en cours (1-18 ou 1-24)
  declaredPoignee?: {
    player: number;
    cards: CardNumber[];
  };
  scores?: GameScore;
}

/** Score d'une donne */
export interface GameScore {
  takerPoints: number;
  defensePoints: number;
  oudlers: number;
  targetPoints: number;
  difference: number;
  contract: Contract;
  multiplier: number;
  poigneeBonus: number;
  petitAuBout: number;
  slamBonus: number;
  finalScores: number[];
}
